import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { DetailCard, FieldRow, Skeleton, StatusPill } from "../../Components/Needed";
import HubFormModal from "./HubFormModal";
import Timeline from "../../Components/Timeline";
import { listBackPath } from "../../Helpers/useListParams";

const formatPreviousValue = (value) => {
    if (value === undefined) return null;
    if (value === null || value === "") return "empty";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const HubDetail = () => {
    const { hubId } = useParams();
    const navigate = useNavigate();
    const [hub, setHub] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [hubResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/staff/hub/${hubId}` }),
            Handler({ method: "GET", url: `/staff/hub/${hubId}/log`, params: { limit: 20 } }),
        ]);
        setHub(hubResponse?.success ? hubResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [hubId]);

    useEffect(() => { load(); }, [load]);

    const toggleStatus = async () => {
        setTogglingStatus(true);
        const response = await Handler({
            method: "patch",
            url: `/staff/hub/${hubId}/status`,
            data: { status: !hub?.status },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={120} />
            </div>
        );
    }

    if (!hub) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Hub not found.</p>
                <button type="button" className="btn back-btn" onClick={() => navigate(listBackPath("/hubs"))}>Back to hubs</button>
            </div>
        );
    }

    return (
        <>
            <button type="button" className="btn back-btn text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/hubs"))}>
                <FaArrowLeft /> Back to hubs
            </button>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <DetailCard title={`${hub.hubId} - ${hub.hubType}`}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <StatusPill active={hub.status} />
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleStatus} disabled={togglingStatus}>
                                {togglingStatus ? "Updating..." : hub.status ? "Deactivate" : "Activate"}
                            </button>
                            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => setShowEditModal(true)}>
                                Edit hub
                            </button>
                        </div>

                        <FieldRow label="Building" value={[hub.buildingNo, hub.buildingName].filter(Boolean).join(" - ")} />
                        <FieldRow label="Street" value={hub.street} />
                        <FieldRow label="Area" value={hub.area} />
                        <FieldRow label="City" value={hub.city} />
                        <FieldRow label="State" value={hub.state} />
                        <FieldRow label="Country" value={hub.country} />
                        <FieldRow label="Pincode" value={hub.pincode} />
                        <FieldRow label="Coordinates" value={`${hub.latitude}, ${hub.longitude}`} />
                        <FieldRow label="In-charge" value={hub.inChargeId} />
                        <FieldRow label="Created" value={formatDateTime(hub.createdAt)} />
                        <FieldRow label="Last updated" value={formatDateTime(hub.updatedAt)} />
                    </DetailCard>
                </div>

                <div className="col-12 col-lg-5">
                    {/* <DetailCard title="Activity Log">
                        {logs.length ? (
                            <ul className="hub-log-list">
                                {logs.map((log) => (
                                    <li key={log._id || `${log.createdAt}-${log.action}`} className="hub-log-item">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className={`badge rounded-pill ${log.action === "Write" ? "text-bg-primary" : "text-bg-secondary"}`}>{log.action}</span>
                                            <span className="text-muted small">{formatDateTime(log.createdAt)}</span>
                                        </div>
                                        <div className="small mt-1">{log?.previousData?.msg || "-"}</div>
                                        {log?.previousData?.data && Object.keys(log.previousData.data).length ? (
                                            <div className="hub-log-diff">
                                                {Object.entries(log.previousData.data).map(([field, value]) => (
                                                    <div key={field}>
                                                        <span className="text-muted">{field}:</span> was {formatPreviousValue(value)}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : ""}
                                        <div className="text-muted small mt-1">By {log.userId}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted mb-0">No activity recorded yet.</p>
                        )}
                    </DetailCard> */}
                    <Timeline userLog={logs} />
                </div>
            </div>

            {showEditModal ? (
                <HubFormModal
                    mode="edit"
                    hub={hub}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default HubDetail;
